// API service for Revive app using native fetch API

// API base URL configuration
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? '' // Empty string will use the proxy from package.json
  : (process.env.REACT_APP_API_URL || 'https://revive-bar-back.onrender.com');

// CORS mode for requests
const CORS_MODE = 'cors';

console.log('API BASE URL:', API_BASE_URL);

// For tracking token refresh attempts
const pendingRefreshPromise = { current: null };
const requestsToRetry = [];

// Function to handle token refresh
const refreshAuthToken = async () => {
  // If there's already a refresh in progress, return that promise
  if (pendingRefreshPromise.current) {
    return pendingRefreshPromise.current;
  }
  
  // Otherwise, start a new refresh
  pendingRefreshPromise.current = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to refresh token');
      }
      
      const data = await response.json();
      
      // Save new tokens
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      
      // Return the new token for use in retried requests
      return data.token;
    } catch (error) {
      // Clear tokens and redirect to login page
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      // Fire storage event to notify components about authentication changes
      window.dispatchEvent(new Event('storage'));
      window.location.href = '/admin';
      throw error;
    } finally {
      // Clear the pending promise
      pendingRefreshPromise.current = null;
    }
  })();
  
  return pendingRefreshPromise.current;
};

// Helper function to get the authentication token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Make a fetch request with proper error handling and token refresh support
const fetchWithAuth = async (url, options = {}, retryCount = 0) => {
  const token = getAuthToken();
  
  // Build request options
  const requestOptions = {
    ...options,
    mode: CORS_MODE,
    credentials: 'include',
    headers: {
      ...options.headers,
    }
  };
  
  // Add authorization header if token exists
  if (token) {
    requestOptions.headers = {
      ...requestOptions.headers,
      'Authorization': `Bearer ${token}`
    };
  }
  
  console.log(`Making ${options.method || 'GET'} request to: ${url}`);

  try {
    const response = await fetch(url, requestOptions);
    
    // Handle token expiration
    if (response.status === 401) {
      const errorData = await response.json();
      
      // Check if this is a token expiration error
      if (errorData.code === 'TOKEN_EXPIRED' && retryCount < 1) {
        try {
          // Refresh the token
          const newToken = await refreshAuthToken();
          
          // Retry the request with the new token
          const newOptions = {
            ...requestOptions,
            headers: {
              ...requestOptions.headers,
              'Authorization': `Bearer ${newToken}`
            }
          };
          
          // Retry the request
          return fetchWithAuth(url, newOptions, retryCount + 1);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw refreshError;
        }
      }
      
      // Other 401 errors or max retries reached
      throw new Error(errorData.message || 'Authentication failed');
    }
    
    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `Request failed with status ${response.status}`);
    }
    
    // Check if the response has JSON content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
};

// Helper for making API requests
const makeApiRequest = async (endpoint, method = 'GET', data = null, options = {}) => {
  let url = `${API_BASE_URL}/api${endpoint}`;
  
  // Configure request options
  const requestOptions = {
    method,
    ...options
  };
  
  // Add data to the request body for non-GET requests
  if (data && method !== 'GET') {
    requestOptions.headers = {
      'Content-Type': 'application/json',
      ...requestOptions.headers
    };
    requestOptions.body = JSON.stringify(data);
  }
  
  // For GET requests, add query parameters to the URL
  if (data && method === 'GET') {
    const queryParams = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      queryParams.append(key, value);
    });
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  return fetchWithAuth(url, requestOptions);
};

// For file uploads with multipart/form-data
const uploadFile = async (endpoint, formData, method = 'POST') => {
  const url = `${API_BASE_URL}/api${endpoint}`;
  
  // Do NOT set Content-Type header, let the browser set it with the boundary
  const requestOptions = {
    method,
    body: formData,
  };
  
  return fetchWithAuth(url, requestOptions);
};

// Authentication functions
export const registerUser = async (userData) => {
  try {
    const response = await makeApiRequest('/auth/register', 'POST', userData);
    
    // Store both tokens
    if (response.token && response.refreshToken) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      
      // Store user data if provided
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    }
    
    return response;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Login user with credentials and optional "remember me" flag
 * @param {Object} credentials - User login credentials
 * @param {boolean} rememberMe - Whether to remember the login (default: true)
 * @returns {Promise<Object>} - Login response data
 */
export const loginUser = async (credentials, rememberMe = true) => {
  try {
    const response = await makeApiRequest('/auth/login', 'POST', credentials);
    
    // Store tokens securely
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken);
    
    // Store admin info if "remember me" is enabled
    if (rememberMe) {
      // Store user data if provided
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      } else {
        // Store basic user info if not provided by API
        localStorage.setItem('adminUser', JSON.stringify({
          email: credentials.email,
          lastLogin: new Date().toISOString(),
          isRemembered: true
        }));
      }
      
      // Set a future date for session expiration (30 days)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);
      localStorage.setItem('adminSessionExpiry', expiryDate.toISOString());
    } else {
      // Clear any existing remembered user
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminSessionExpiry');
    }
    
    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout user and clear all auth data
 * @param {boolean} forgetUser - Whether to forget the remembered user (default: false)
 */
export const logoutUser = async (forgetUser = false) => {
  try {
    // Call the logout endpoint to invalidate the token on the server
    const token = localStorage.getItem('token');
    
    if (token) {
      // Attempt to call the logout endpoint
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => {
        // Ignore server errors during logout - we'll still clear local tokens
        console.warn('Error during server logout:', error);
      });
    }
  } catch (error) {
    console.warn('Error during logout process:', error);
  } finally {
    // Always clear both tokens
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // If forgetUser is true, also clear user data and preferences
    if (forgetUser) {
      localStorage.removeItem('user');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('adminSessionExpiry');
    }
  }
};

// Product functions
export const fetchProducts = async () => {
  try {
    console.log('Fetching products');
    
    const result = await makeApiRequest('/products', 'GET');
    
    // According to API documentation, response should have this structure:
    // { success: boolean, count: number, data: Array }
    console.log('API response structure:', {
      success: result.success,
      count: result.count,
      hasData: Boolean(result.data),
      dataType: result.data ? (Array.isArray(result.data) ? 'array' : typeof result.data) : 'undefined'
    });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Check if data is missing
    if (!result.data) {
      console.warn('API response is missing the data property');
      return {
        smoothies: [],
        juices: [],
        proteinBalls: []
      };
    }
    
    // Extract the products array from the data property
    const products = result.data;
    
    if (!Array.isArray(products)) {
      console.error('API data property is not an array:', products);
      return {
        smoothies: [],
        juices: [],
        proteinBalls: []
      };
    }
    
    console.log(`Fetched ${products.length} products from API`);
    
    // Transform the flat products array into categorized structure
    const categorized = {
      smoothies: products
        .filter(p => p && p.category === 'smoothies')
        .map(p => ({
          ...p,
          // Ensure products have both id and _id fields for frontend compatibility
          id: p.id || p._id,
          _id: p._id || p.id
        })),
      juices: products
        .filter(p => p && p.category === 'juices')
        .map(p => ({
          ...p,
          id: p.id || p._id,
          _id: p._id || p.id
        })),
      proteinBalls: products
        .filter(p => p && p.category === 'proteinBalls')
        .map(p => ({
          ...p,
          id: p.id || p._id,
          _id: p._id || p.id
        }))
    };
    
    console.log('Categorized products:', {
      smoothies: categorized.smoothies.length,
      juices: categorized.juices.length,
      proteinBalls: categorized.proteinBalls.length
    });
    
    return categorized;
  } catch (error) {
    console.error('Error fetching products:', error);
    // Return empty categorized structure on error
    return {
      smoothies: [],
      juices: [],
      proteinBalls: []
    };
  }
};

export const fetchProductById = async (productId) => {
  try {
    // Use _id if it's a product object, otherwise use the ID directly
    const idForBackend = productId._id || productId;
    console.log(`Fetching product with ID: ${idForBackend}`);
    
    const result = await makeApiRequest(`/products/${idForBackend}`, 'GET');
    
    // According to API documentation, response should have this structure:
    // { success: boolean, data: Object }
    console.log('Product API response structure:', {
      success: result.success,
      hasData: Boolean(result.data),
      dataType: result.data ? typeof result.data : 'undefined'
    });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Extract the product from the data property
    const product = result.data;
    
    if (!product) {
      throw new Error('API returned empty product data');
    }
    
    // Ensure consistent ID format for frontend
    const formattedProduct = {
      ...product,
      id: product.id || product._id,
      _id: product._id || product.id
    };
    
    console.log('Formatted product:', formattedProduct);
    return formattedProduct;
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};

// The uploadProductImage function has been removed and integrated directly
// into the createProduct and updateProduct functions to ensure proper handling
// of multipart/form-data requests according to the backend requirements

export const createProduct = async (productData, imageFile = null) => {
  try {
    // Ensure nutritionFacts is complete to pass validation
    const nutritionFacts = {
      calories: 0,
      protein: 0,
      fat: 0,
      carbs: 0,
      ...(productData.nutritionFacts || {})
    };
    
    // Format the product data for the backend
    const formattedData = {
      ...productData,
      nutritionFacts,
      // Initialize inventory data if not provided
      inventory: productData.inventory || {
        inStock: true,
        stockQuantity: 0,
        lowStockThreshold: 5,
        sku: `SKU-${Date.now().toString().slice(-6)}`,
        lastRestocked: new Date().toISOString()
      }
    };
    
    // If we're using frontend 'id', map it to '_id' for backend
    // or remove it to let backend generate its own ID
    if (formattedData.id) {
      // Remove client-side id to avoid conflicts
      delete formattedData.id; // Let backend generate its own ID
    }
    
    console.log('Creating product with data:', formattedData);
    
    // If an image file is provided, use FormData to send multipart request
    if (imageFile) {
      // Create FormData object for multipart/form-data request
      const formData = new FormData();
      
      // Add the image file
      formData.append('image', imageFile);
      
      // Add all product fields as form fields
      formData.append('name', formattedData.name);
      formData.append('description', formattedData.description);
      formData.append('price', formattedData.price);
      formData.append('category', formattedData.category);
      
      // Add complex objects as stringified JSON
      formData.append('ingredients', JSON.stringify(formattedData.ingredients));
      formData.append('nutritionFacts', JSON.stringify(formattedData.nutritionFacts));
      formData.append('inventory', JSON.stringify(formattedData.inventory));
      
      // Log FormData for debugging
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        if (pair[0] === 'image') {
          console.log(pair[0], pair[1].name, pair[1].type, pair[1].size + ' bytes');
        } else {
          console.log(pair[0], pair[1]);
        }
      }
      
      // Use dedicated file upload function
      const result = await uploadFile('/products', formData);
      return formatProductData(result.data, 'create');
    } else {
      // Standard JSON request for product creation without image
      const result = await makeApiRequest('/products', 'POST', formattedData);
      return formatProductData(result.data, 'create');
    }
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Helper function to format product data from API responses
 */
const formatProductData = (product, operation) => {
  if (!product) {
    throw new Error(`API returned empty product data after ${operation}`);
  }
  
  // Ensure consistent ID format for frontend
  const formattedProduct = {
    ...product,
    id: product.id || product._id,
    _id: product._id || product.id
  };
  
  console.log(`Successfully ${operation}d product:`, formattedProduct);
  return formattedProduct;
};

export const updateProduct = async (productId, productData, imageFile = null) => {
  try {
    // Format the product data for the backend
    // The backend expects _id or will use its own ID system
    const formattedData = {
      ...productData,
      // Preserve existing inventory data if not provided
      inventory: productData.inventory || {
        inStock: true,
        stockQuantity: 0,
        lowStockThreshold: 5,
        sku: `SKU-${Date.now().toString().slice(-6)}`,
        lastRestocked: new Date().toISOString()
      }
    };

    // Remove any ID fields to avoid conflicts with backend
    // The productId parameter will be used in the URL
    if (formattedData.id) delete formattedData.id;
    if (formattedData._id) delete formattedData._id;
    
    console.log(`Updating product ${productId} with data:`, formattedData);
    
    // If an image file is provided, use FormData to send multipart request
    if (imageFile) {
      // Create FormData object for multipart/form-data request
      const formData = new FormData();
      
      // Add the image file
      formData.append('image', imageFile);
      
      // Add all product fields as form fields
      formData.append('name', formattedData.name);
      formData.append('description', formattedData.description);
      formData.append('price', formattedData.price);
      formData.append('category', formattedData.category);
      
      // Ensure nutritionFacts object is complete
      const nutritionFacts = {
        calories: 0, 
        protein: 0, 
        fat: 0, 
        carbs: 0,
        ...(formattedData.nutritionFacts || {})
      };
      
      // Add complex objects as stringified JSON
      formData.append('ingredients', JSON.stringify(formattedData.ingredients));
      formData.append('nutritionFacts', JSON.stringify(nutritionFacts));
      formData.append('inventory', JSON.stringify(formattedData.inventory));
      
      // Log FormData for debugging
      console.log('FormData entries for update:');
      for (let pair of formData.entries()) {
        if (pair[0] === 'image') {
          console.log(pair[0], pair[1].name, pair[1].type, pair[1].size + ' bytes');
        } else {
          console.log(pair[0], pair[1]);
        }
      }
      
      // Use dedicated file upload function with PUT method
      const result = await uploadFile(`/products/${productId}`, formData, 'PUT');
      return formatProductData(result.data, 'update');
    } else {
      // Ensure nutritionFacts object is complete for validation
      const completeFormattedData = {
        ...formattedData,
        nutritionFacts: {
          calories: 0,
          protein: 0,
          fat: 0,
          carbs: 0,
          ...(formattedData.nutritionFacts || {})
        }
      };
      
      // Standard JSON request for product update without image
      const result = await makeApiRequest(`/products/${productId}`, 'PUT', completeFormattedData);
      return formatProductData(result.data, 'update');
    }
  } catch (error) {
    console.error(`Error updating product ${productId}:`, error);
    throw error;
  }
};

export const deleteProduct = async (productId) => {
  try {
    // If productId contains _id, use that, otherwise use productId directly
    const idForBackend = productId._id || productId;
    
    console.log(`Deleting product with ID: ${idForBackend}`);
    
    const result = await makeApiRequest(`/products/${idForBackend}`, 'DELETE');
    
    console.log(`Successfully deleted product with ID: ${idForBackend}`);
    return true;
  } catch (error) {
    console.error(`Error deleting product ${productId}:`, error);
    throw error;
  }
};

// Cart functions
export const fetchCart = async () => {
  try {
    const result = await makeApiRequest('/cart', 'GET');
    
    // According to API documentation, response should have this structure:
    // { success: boolean, data: Object }
    console.log('Cart API response structure:', {
      success: result.success,
      hasData: Boolean(result.data)
    });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the cart data
    return result.data;
  } catch (error) {
    console.error('Error fetching cart:', error);
    throw error;
  }
};

export const addToCart = async (productId, quantity) => {
  try {
    const result = await makeApiRequest('/cart', 'POST', { productId, quantity });
    
    // Verify the success flag
    if (!result.success) {
      // Check for inventory-specific errors in the success response
      if (result.error && result.error.includes('stock')) {
        throw new Error(result.error);
      }
      
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the updated cart data
    return result.data;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

export const updateCartItem = async (productId, quantity) => {
  try {
    const result = await makeApiRequest(`/cart/${productId}`, 'PUT', { quantity });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the updated cart data
    return result.data;
  } catch (error) {
    console.error(`Error updating cart item ${productId}:`, error);
    throw error;
  }
};

export const removeFromCart = async (productId) => {
  try {
    const result = await makeApiRequest(`/cart/${productId}`, 'DELETE');
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    return true;
  } catch (error) {
    console.error(`Error removing from cart ${productId}:`, error);
    throw error;
  }
};

// Order functions
export const createOrder = async (orderData) => {
  try {
    const result = await makeApiRequest('/orders', 'POST', orderData);
    
    // Verify the success flag
    if (!result.success) {
      // Check for inventory-specific errors in the success response
      if (result.error && result.error.includes('stock')) {
        const match = result.error.match(/Product "(.*?)" is/);
        const productName = match ? match[1] : 'A product';
        throw new Error(`${result.error || `${productName} is out of stock or has insufficient quantity`}`);
      }
      
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the created order data
    return result.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const fetchOrders = async () => {
  try {
    const result = await makeApiRequest('/orders', 'GET');
    
    // According to API documentation, response should have this structure:
    // { success: boolean, count: number, data: Array }
    console.log('Orders API response structure:', {
      success: result.success,
      count: result.count,
      hasData: Boolean(result.data),
      dataType: result.data ? (Array.isArray(result.data) ? 'array' : typeof result.data) : 'undefined'
    });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the orders data (array)
    return result.data || [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    // Return empty array on error for easier handling in components
    return [];
  }
};

export const fetchOrderById = async (orderId) => {
  try {
    const result = await makeApiRequest(`/orders/${orderId}`, 'GET');
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the order data
    return result.data;
  } catch (error) {
    console.error(`Error fetching order ${orderId}:`, error);
    throw error;
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const result = await makeApiRequest(`/orders/${orderId}/status`, 'PUT', { status });
    
    // Verify the success flag
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    // Return the updated order data
    return result.data;
  } catch (error) {
    console.error(`Error updating order status ${orderId}:`, error);
    throw error;
  }
};

// Inventory management functions

/**
 * Fetches inventory data for all products
 * @returns {Promise<Array>} - Array of inventory items
 */
export const fetchInventory = async () => {
  try {
    const result = await makeApiRequest('/inventory', 'GET');
    
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching inventory:', error);
    return [];
  }
};

/**
 * Fetches inventory data for a specific product
 * @param {string} productId - The product ID to fetch inventory for
 * @returns {Promise<Object>} - Inventory data for the product
 */
export const fetchProductInventory = async (productId) => {
  try {
    const result = await makeApiRequest(`/inventory/${productId}`, 'GET');
    
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    return result.data;
  } catch (error) {
    console.error(`Error fetching inventory for product ${productId}:`, error);
    throw error;
  }
};

/**
 * Updates inventory for a specific product
 * @param {string} productId - The product ID to update inventory for
 * @param {Object} inventoryData - The inventory data to update
 * @returns {Promise<Object>} - Updated inventory data
 */
export const updateInventory = async (productId, inventoryData) => {
  try {
    const result = await makeApiRequest(`/inventory/${productId}`, 'PUT', inventoryData);
    
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    return result.data;
  } catch (error) {
    console.error(`Error updating inventory for product ${productId}:`, error);
    throw error;
  }
};

/**
 * Restocks a product to a specified quantity
 * @param {string} productId - The product ID to restock
 * @param {number} quantity - The quantity to add to current stock
 * @returns {Promise<Object>} - Updated inventory data
 */
export const restockProduct = async (productId, quantity) => {
  try {
    console.log(`Attempting to restock product ID: ${productId} with quantity: ${quantity}`);
    
    // Check if we're using old or new ID format - handle case when productId is an object
    const id = typeof productId === 'object' && productId !== null ? (productId._id || productId.id) : productId;
    
    // First, get the current product to verify it exists
    const productResponse = await makeApiRequest(`/products/${id}`, 'GET').catch(err => {
      console.warn(`Product fetch failed: ${err.message}`);
      return null;
    });
    
    if (!productResponse || !productResponse.data) {
      console.error('Product not found during restock operation');
      throw new Error('Product not found or not accessible');
    }
    
    // Now proceed with the restock operation
    const result = await makeApiRequest(`/inventory/${id}/restock`, 'POST', { quantity });
    
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    console.log(`Successfully restocked product ID: ${id}`);
    return result.data;
  } catch (error) {
    console.error(`Error restocking product ${productId}:`, error);
    throw error;
  }
};

/**
 * Fetches products with low stock
 * @returns {Promise<Array>} - Array of products with stock below their threshold
 */
export const fetchLowStockProducts = async () => {
  try {
    const result = await makeApiRequest('/inventory/low-stock', 'GET');
    
    if (!result.success) {
      throw new Error(result.error || 'API indicated unsuccessful response');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching low stock products:', error);
    return [];
  }
};

